import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WestRoundedIcon from '@mui/icons-material/WestRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import * as XLSX from 'xlsx';

import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import PageContainer from '../common/PageContainer';
import StudentResultsSimpleTable from './results/StudentResultsSimpleTable';
import ClassAnalyticsPanel from './results/ClassAnalyticsPanel';

const FILTER_STORAGE_PREFIX = 'academic-results-filters';

const getSafe = (value, fallback = '-') => (value ?? fallback);

const deriveOverallStatus = (student) => {
  const studentStatus = String(student?.status || '').toLowerCase();
  const subjectResults = Array.isArray(student?.subjectResults) ? student.subjectResults : [];
  const percentage = student?.percentage;
  const totalMarks = student?.totalMarks;

  if (studentStatus === 'absent') return 'Absent';
  if (studentStatus === 'fail' || studentStatus === 'failed') return 'Failed';
  if (studentStatus === 'na' || studentStatus === 'n/a' || studentStatus === 'not available') return 'N/A';

  const hasFailedSubject = subjectResults.some((subj) =>
    typeof subj.marksObtained === 'number'
    && typeof subj.passMarks === 'number'
    && subj.marksObtained < subj.passMarks
  );
  if (hasFailedSubject) return 'Failed';

  if (typeof percentage === 'number' && percentage < 40 && studentStatus !== 'passed') {
    return 'Failed';
  }

  if (subjectResults.length === 0 && (totalMarks == null || totalMarks === 0)) {
    return 'N/A';
  }

  return 'Passed';
};

const deriveSubjectStatus = (subject, student) => {
  const directStatus = String(subject?.status || '').trim();
  if (directStatus) {
    if (directStatus.toLowerCase().includes('pass')) return 'Pass';
    if (directStatus.toLowerCase().includes('fail')) return 'Fail';
    return directStatus;
  }
  if (subject?.marksObtained == null) {
    return deriveOverallStatus(student) === 'Absent' ? 'Absent' : 'N/A';
  }
  if (typeof subject?.passMarks === 'number') {
    return subject.marksObtained >= subject.passMarks ? 'Pass' : 'Fail';
  }
  return deriveOverallStatus(student) === 'Passed' ? 'Pass' : 'Fail';
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Dates not available';
  return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
};

function ExamResults() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { examinationTypeId: routeExaminationTypeId } = useParams();

  const schoolId = localStorage.getItem('schoolId');
  const savedFilterKey = `${FILTER_STORAGE_PREFIX}:${schoolId || 'default'}`;

  const [examTypes, setExamTypes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [scheduleOverview, setScheduleOverview] = useState({ loading: false, groups: [], scheduleMode: 'CLASS_WISE' });
  const [allResults, setAllResults] = useState([]);

  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [examinationTypeId, setExaminationTypeId] = useState(routeExaminationTypeId || '');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rollNumberFilter, setRollNumberFilter] = useState('');
  const [rankBucket, setRankBucket] = useState('ALL');
  const [sortBy, setSortBy] = useState('RANK');
  const [activeTab, setActiveTab] = useState('results');
  const [reportCardTemplate, setReportCardTemplate] = useState('MODERN');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [downloadingReportCard, setDownloadingReportCard] = useState(null);
  const [downloadingMarksheetPdf, setDownloadingMarksheetPdf] = useState(false);
  const [downloadingMarksheetExcel, setDownloadingMarksheetExcel] = useState(false);
  const [sendingBulkSms, setSendingBulkSms] = useState(false);
  const [bulkSmsDialogOpen, setBulkSmsDialogOpen] = useState(false);
  const [bulkSmsMessage, setBulkSmsMessage] = useState('');
  const [studentContactCache, setStudentContactCache] = useState({});
  const [detailStudent, setDetailStudent] = useState(null);

  const deferredSearchTerm = useDeferredValue(searchTerm);

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const selectedExamDetails = useMemo(
    () => examTypes.find((exam) => String(exam.examinationTypeId) === String(examinationTypeId)) || null,
    [examTypes, examinationTypeId]
  );

  const scheduleAssignments = useMemo(
    () => scheduleOverview.groups.flatMap((group) => group.assignments || []),
    [scheduleOverview.groups]
  );

  const classOptions = useMemo(() => {
    if (!examinationTypeId || !scheduleAssignments.length) {
      return classes;
    }
    const allowedIds = new Set(scheduleAssignments.map((item) => String(item.classId)));
    return classes.filter((item) => allowedIds.has(String(item.classId)));
  }, [classes, scheduleAssignments, examinationTypeId]);

  const sectionOptions = useMemo(() => {
    if (!classId) return [];
    if (scheduleAssignments.length) {
      const allowedSections = scheduleAssignments.filter((item) => String(item.classId) === String(classId));
      const uniqueSectionIds = new Set(allowedSections.map((item) => String(item.sectionId)));
      return allSections.filter((item) => uniqueSectionIds.has(String(item.sectionId)));
    }
    return allSections.filter((item) => String(item.classId) === String(classId));
  }, [allSections, classId, scheduleAssignments]);

  const selectedClassName = useMemo(
    () => classOptions.find((item) => String(item.classId) === String(classId))?.className || 'N/A',
    [classOptions, classId]
  );

  const selectedSectionName = useMemo(
    () => sectionOptions.find((item) => String(item.sectionId) === String(sectionId))?.sectionName || 'N/A',
    [sectionOptions, sectionId]
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingInitialData(true);
      setError('');
      try {
        const [examTypesResp, classesResp, sectionsResp] = await Promise.all([
          axiosInstance.get(`/api/examination-types/school/${schoolId}`),
          axiosInstance.get('/api/classes'),
          axiosInstance.get('/api/sections', { headers: { schoolId } }),
        ]);

        const sortedExams = (examTypesResp.data || [])
          .slice()
          .sort((a, b) => new Date(b.startDate || b.createdAt || 0) - new Date(a.startDate || a.createdAt || 0));

        setExamTypes(sortedExams);
        setClasses(classesResp.data || []);
        setAllSections(Array.isArray(sectionsResp.data) ? sectionsResp.data : []);

        const savedFilters = routeExaminationTypeId
          ? null
          : JSON.parse(localStorage.getItem(savedFilterKey) || 'null');

        if (savedFilters) {
          setExaminationTypeId(String(savedFilters.examinationTypeId || ''));
          setClassId(String(savedFilters.classId || ''));
          setSectionId(String(savedFilters.sectionId || ''));
          setSubjectId(String(savedFilters.subjectId || ''));
          setSearchTerm(savedFilters.searchTerm || '');
          setRollNumberFilter(savedFilters.rollNumberFilter || '');
          setRankBucket(savedFilters.rankBucket || 'ALL');
          setSortBy(savedFilters.sortBy || 'RANK');
          setActiveTab(savedFilters.activeTab || 'results');
          setReportCardTemplate(savedFilters.reportCardTemplate || 'MODERN');
        } else if (routeExaminationTypeId) {
          setExaminationTypeId(String(routeExaminationTypeId));
        }
      } catch (err) {
        const message = getApiErrorMessage(err, 'Failed to load results filters.');
        setError(message);
        showNotification(message, 'error');
      } finally {
        setLoadingInitialData(false);
      }
    };

    if (schoolId) {
      fetchInitialData();
    } else {
      setError('School ID is missing. Please log in again.');
      setLoadingInitialData(false);
    }
  }, [routeExaminationTypeId, savedFilterKey, schoolId, showNotification]);

  useEffect(() => {
    if (!schoolId) return;
    localStorage.setItem(
      savedFilterKey,
      JSON.stringify({
        examinationTypeId,
        classId,
        sectionId,
        subjectId,
        searchTerm,
        rollNumberFilter,
        rankBucket,
        sortBy,
        activeTab,
        reportCardTemplate,
      })
    );
  }, [savedFilterKey, schoolId, examinationTypeId, classId, sectionId, subjectId, searchTerm, rollNumberFilter, rankBucket, sortBy, activeTab, reportCardTemplate]);

  useEffect(() => {
    const fetchScheduleOverview = async () => {
      if (!examinationTypeId) {
        setScheduleOverview({ loading: false, groups: [], scheduleMode: 'CLASS_WISE' });
        return;
      }

      setScheduleOverview((prev) => ({ ...prev, loading: true }));
      try {
        const response = await axiosInstance.get(`/api/examination-types/${examinationTypeId}/schedule/overview`);
        const overview = response.data || {};
        setScheduleOverview({
          loading: false,
          groups: Array.isArray(overview.groups) ? overview.groups : [],
          scheduleMode: overview.scheduleMode || 'CLASS_WISE',
        });
      } catch (err) {
        setScheduleOverview({ loading: false, groups: [], scheduleMode: 'CLASS_WISE' });
      }
    };

    fetchScheduleOverview();
  }, [examinationTypeId]);

  useEffect(() => {
    if (!classOptions.length) {
      setClassId('');
      setSectionId('');
      return;
    }
    if (classId && classOptions.some((item) => String(item.classId) === String(classId))) {
      return;
    }
    setClassId(String(classOptions[0].classId));
  }, [classId, classOptions]);

  useEffect(() => {
    if (!sectionOptions.length) {
      setSectionId('');
      return;
    }
    if (sectionId && sectionOptions.some((item) => String(item.sectionId) === String(sectionId))) {
      return;
    }
    setSectionId(String(sectionOptions[0].sectionId));
  }, [sectionId, sectionOptions]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!examinationTypeId || !classId || !sectionId) {
        setAllResults([]);
        setSelectedStudentIds([]);
        return;
      }

      setLoadingResults(true);
      setError('');
      try {
        const response = await axiosInstance.get('/api/students/exam-results', {
          params: {
            examinationTypeId,
            classId,
            sectionId,
          },
        });

        const nextResults = Array.isArray(response.data) ? response.data : [];
        setAllResults(nextResults.map((student) => ({
          ...student,
          subjectResults: Array.isArray(student.subjectResults) ? student.subjectResults : [],
        })));
        setSelectedStudentIds([]);
      } catch (err) {
        const message = getApiErrorMessage(err, 'Failed to fetch exam results.');
        setAllResults([]);
        setError(message);
        showNotification(message, 'error');
      } finally {
        setLoadingResults(false);
      }
    };

    fetchResults();
  }, [classId, examinationTypeId, sectionId, showNotification]);

  const subjectOptions = useMemo(() => {
    const subjectMap = new Map();

    const relevantGroups = scheduleOverview.groups.filter((group) => {
      const assignments = Array.isArray(group.assignments) ? group.assignments : [];
      if (!assignments.length) return true;
      const matchesClass = !classId || assignments.some((item) => String(item.classId) === String(classId));
      const matchesSection = !sectionId || assignments.some((item) => String(item.sectionId) === String(sectionId));
      return matchesClass && matchesSection;
    });

    relevantGroups.forEach((group) => {
      (group.slots || []).forEach((slot) => {
        const nextId = String(slot.subjectId || slot.subjectName || '');
        if (!nextId || subjectMap.has(nextId)) return;
        subjectMap.set(nextId, {
          subjectId: nextId,
          subjectName: slot.subjectName || 'Subject',
        });
      });
    });

    if (!subjectMap.size) {
      allResults.forEach((student) => {
        (student.subjectResults || []).forEach((subject) => {
          const nextId = String(subject.subjectId || subject.subjectName || '');
          if (!nextId || subjectMap.has(nextId)) return;
          subjectMap.set(nextId, {
            subjectId: nextId,
            subjectName: subject.subjectName || 'Subject',
          });
        });
      });
    }

    return Array.from(subjectMap.values()).sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [allResults, classId, scheduleOverview.groups, sectionId]);

  useEffect(() => {
    if (!subjectId) return;
    if (!subjectOptions.some((item) => String(item.subjectId) === String(subjectId))) {
      setSubjectId('');
    }
  }, [subjectId, subjectOptions]);

  const studentRows = useMemo(() => {
    const search = deferredSearchTerm.trim().toLowerCase();
    const rollSearch = rollNumberFilter.trim().toLowerCase();

    const rankedRows = allResults.map((student) => {
      const subjects = Array.isArray(student.subjectResults) && student.subjectResults.length
        ? student.subjectResults
        : [{
          subjectId: 'overall',
          subjectName: 'Overall',
          marksObtained: student.totalMarks,
          maxMarks: student.totalMaxMarks,
          percentage: student.percentage,
          grade: student.overallGrade,
          passMarks: null,
          status: student.status,
        }];

      const filteredSubjects = subjects
        .filter((subject) => !subjectId || String(subject.subjectId || subject.subjectName) === String(subjectId))
        .map((subject) => {
          const percentage = typeof subject.percentage === 'number'
            ? subject.percentage
            : (typeof subject.marksObtained === 'number' && typeof subject.maxMarks === 'number' && subject.maxMarks > 0
              ? (subject.marksObtained / subject.maxMarks) * 100
              : null);

          return {
            rowId: `${student.studentId}-${subject.subjectId || subject.subjectName}`,
            studentId: student.studentId,
            studentName: student.studentName || 'Student',
            profileImageUrl: student.profileImageUrl,
            admissionNumber: student.admissionNumber || '-',
            rollNumber: student.rollNumber || student.admissionNumber || '-',
            classSection: `${selectedClassName} • ${selectedSectionName}`,
            subjectId: String(subject.subjectId || subject.subjectName),
            subjectName: subject.subjectName || 'Subject',
            marksObtained: subject.marksObtained,
            totalMarks: subject.maxMarks,
            percentage,
            grade: subject.grade || student.overallGrade || '-',
            status: deriveSubjectStatus(subject, student),
            studentResult: student,
            subjectResult: subject,
          };
        });

      if (!filteredSubjects.length) {
        return null;
      }

      const summarySubject = filteredSubjects[0];
      const summaryMarksObtained = subjectId
        ? summarySubject?.marksObtained
        : student.totalMarks;
      const summaryTotalMarks = subjectId
        ? summarySubject?.totalMarks
        : student.totalMaxMarks;
      const summaryPercentage = subjectId
        ? summarySubject?.percentage
        : student.percentage;
      const summaryGrade = subjectId
        ? summarySubject?.grade
        : student.overallGrade || '-';
      const summaryStatus = subjectId
        ? summarySubject?.status
        : deriveOverallStatus(student);

      return {
        rowId: String(student.studentId),
        studentId: student.studentId,
        studentName: student.studentName || 'Student',
        profileImageUrl: student.profileImageUrl,
        admissionNumber: student.admissionNumber || '-',
        rollNumber: student.rollNumber || student.admissionNumber || '-',
        classSection: `${selectedClassName} • ${selectedSectionName}`,
        marksObtained: summaryMarksObtained,
        totalMarks: summaryTotalMarks,
        percentage: summaryPercentage,
        grade: summaryGrade,
        status: summaryStatus,
        subjectCount: filteredSubjects.length,
        subjectRows: filteredSubjects,
        studentResult: student,
      };
    }).filter((row) => {
      if (!row) return false;
      const matchesSearch = !search || [
        row.studentName,
        row.admissionNumber,
        row.rollNumber,
        ...(row.subjectRows || []).map((subject) => subject.subjectName),
      ].some((value) => String(value || '').toLowerCase().includes(search));
      const matchesRoll = !rollSearch || String(row.rollNumber || '').toLowerCase().includes(rollSearch);
      return matchesSearch && matchesRoll;
    });

    const sortedForRanking = rankedRows
      .slice()
      .sort((a, b) => {
        const aPct = typeof a.percentage === 'number' ? a.percentage : -1;
        const bPct = typeof b.percentage === 'number' ? b.percentage : -1;
        if (bPct !== aPct) return bPct - aPct;
        return String(a.studentName || '').localeCompare(String(b.studentName || ''));
      })
      .map((row, index) => ({
        ...row,
        rank: index + 1,
      }));

    const applyRankBucket = (rows) => {
      switch (rankBucket) {
        case 'TOP_3':
          return rows.filter((row) => row.rank <= 3);
        case 'TOP_10':
          return rows.filter((row) => row.rank <= 10);
        case 'TOP_20':
          return rows.filter((row) => row.rank <= 20);
        case 'BOTTOM_10':
          return rows.filter((row) => row.rank > Math.max(rows.length - 10, 0));
        default:
          return rows;
      }
    };

    const filteredByRank = applyRankBucket(sortedForRanking);

    return filteredByRank.slice().sort((a, b) => {
      if (sortBy === 'ROLL') {
        return String(a.rollNumber || '').localeCompare(String(b.rollNumber || ''), undefined, { numeric: true });
      }
      if (sortBy === 'NAME') {
        return String(a.studentName || '').localeCompare(String(b.studentName || ''));
      }
      return (a.rank || 0) - (b.rank || 0);
    });
  }, [allResults, deferredSearchTerm, rollNumberFilter, selectedClassName, selectedSectionName, subjectId, rankBucket, sortBy]);

  const analytics = useMemo(() => {
    const validStudents = studentRows.filter((student) => typeof student.percentage === 'number');
    const classTopper = validStudents[0] || null;
    const averagePerformance = validStudents.length
      ? validStudents.reduce((sum, item) => sum + (item.percentage || 0), 0) / validStudents.length
      : null;
    const passCount = studentRows.filter((student) => deriveOverallStatus(student.studentResult || student) === 'Passed').length;
    const failCount = studentRows.filter((student) => deriveOverallStatus(student.studentResult || student) === 'Failed').length;
    const passPercentage = studentRows.length ? (passCount / studentRows.length) * 100 : null;

    const subjectMap = new Map();
    studentRows.forEach((row) => {
      (row.subjectRows || []).forEach((subject) => {
        if (typeof subject.percentage !== 'number') return;
        const current = subjectMap.get(subject.subjectName) || { total: 0, count: 0 };
        subjectMap.set(subject.subjectName, { total: current.total + subject.percentage, count: current.count + 1 });
      });
    });

    const subjectChartData = Array.from(subjectMap.entries())
      .map(([subjectName, summary]) => ({
        subjectName,
        averagePercentage: summary.count ? Number((summary.total / summary.count).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.averagePercentage - a.averagePercentage)
      .slice(0, 8);

    const gradeMap = new Map();
    studentRows.forEach((student) => {
      const grade = student.grade || 'N/A';
      gradeMap.set(grade, (gradeMap.get(grade) || 0) + 1);
    });

    return {
      classTopper,
      averagePerformance,
      passPercentage,
      passCount,
      failCount,
      subjectChartData,
      gradeDistribution: Array.from(gradeMap.entries()).map(([grade, count]) => ({ grade, count })),
    };
  }, [studentRows]);

  const activeStudentIds = useMemo(
    () => Array.from(new Set(studentRows.map((row) => String(row.studentId)))),
    [studentRows]
  );

  const selectedCount = selectedStudentIds.length;
  const selectedExamLabel = selectedExamDetails?.examinationTypeName || 'Select exam';

  useEffect(() => {
    setBulkSmsMessage(
      `Dear Parent, ${selectedExamLabel} results for ${selectedClassName} ${selectedSectionName} are now available in the school portal. Please review the report card for detailed marks and remarks.`
    );
  }, [selectedClassName, selectedExamLabel, selectedSectionName]);

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds((prev) => (
      prev.includes(String(studentId))
        ? prev.filter((item) => item !== String(studentId))
        : [...prev, String(studentId)]
    ));
  };

  const toggleSelectAllVisible = () => {
    setSelectedStudentIds((prev) => (
      prev.length === activeStudentIds.length ? [] : activeStudentIds
    ));
  };

  const handleCloseNotification = (_, reason) => {
    if (reason === 'clickaway') return;
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const getFileNameFromDisposition = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;
    const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    if (!match || !match[1]) return fallback;
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  };

  const downloadBlobFile = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadMarksheetPdf = async () => {
    if (!examinationTypeId || !classId || !sectionId) {
      showNotification('Select exam, class, and section first.', 'warning');
      return;
    }
    try {
      setDownloadingMarksheetPdf(true);
      const response = await axiosInstance.get('/api/school/reports/filtered-results-pdf', {
        params: { examinationTypeId, classId, sectionId },
        responseType: 'blob',
      });
      const fallback = `${selectedExamLabel}_${selectedClassName}_${selectedSectionName}_Marksheet.pdf`.replace(/\s+/g, '_');
      const fileName = getFileNameFromDisposition(response.headers['content-disposition'], fallback);
      downloadBlobFile(response.data, fileName);
      showNotification('Marksheet PDF downloaded successfully.', 'success');
    } catch (err) {
      showNotification(getApiErrorMessage(err, 'Failed to download marksheet PDF.'), 'error');
    } finally {
      setDownloadingMarksheetPdf(false);
    }
  };

  const handleDownloadMarksheetExcel = async () => {
    if (!examinationTypeId || !classId || !sectionId) {
      showNotification('Select exam, class, and section first.', 'warning');
      return;
    }
    try {
      setDownloadingMarksheetExcel(true);
      const response = await axiosInstance.get('/api/school/reports/class-results-excel', {
        params: { examinationTypeId, classId, sectionId },
        responseType: 'blob',
      });
      const fallback = `${selectedExamLabel}_${selectedClassName}_${selectedSectionName}_Marksheet.xlsx`.replace(/\s+/g, '_');
      const fileName = getFileNameFromDisposition(response.headers['content-disposition'], fallback);
      downloadBlobFile(response.data, fileName);
      showNotification('Marksheet Excel downloaded successfully.', 'success');
    } catch (err) {
      showNotification(getApiErrorMessage(err, 'Failed to download marksheet Excel.'), 'error');
    } finally {
      setDownloadingMarksheetExcel(false);
    }
  };

  const handleExportVisibleRows = () => {
    if (!studentRows.length) {
      showNotification('No result rows available to export.', 'warning');
      return;
    }

    const sheetRows = studentRows.map((row) => ({
      'Roll No': row.rollNumber,
      'Student Name': row.studentName,
      'Class & Section': row.classSection,
      Subjects: row.subjectCount,
      'Marks Obtained': row.marksObtained ?? '-',
      'Total Marks': row.totalMarks ?? '-',
      Percentage: typeof row.percentage === 'number' ? `${row.percentage.toFixed(1)}%` : '-',
      Grade: row.grade,
      Status: row.status,
      'Subject Breakdown': (row.subjectRows || [])
        .map((subject) => `${subject.subjectName}: ${subject.marksObtained ?? '-'} / ${subject.totalMarks ?? '-'} (${typeof subject.percentage === 'number' ? `${subject.percentage.toFixed(1)}%` : '-'})`)
        .join(', '),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Results');
    XLSX.writeFile(workbook, `${selectedExamLabel}_${selectedClassName}_${selectedSectionName}_VisibleRows.xlsx`.replace(/\s+/g, '_'));
    showNotification('Visible result rows exported successfully.', 'success');
  };

  const handleDownloadReportCard = async (row) => {
    const student = row?.studentResult || row;
    if (!student?.studentId || !selectedExamDetails?.examinationTypeId || !classId || !sectionId) {
      showNotification('Cannot generate report card. Missing required details.', 'warning');
      return;
    }

    try {
      setDownloadingReportCard(student.studentId);
      const response = await axiosInstance.get(`/api/school/reports/result-card/${student.studentId}/download/pdf`, {
        params: {
          classId,
          sectionId,
          examinationTypeId: selectedExamDetails.examinationTypeId,
          template: reportCardTemplate,
        },
        responseType: 'blob',
      });
      const fallback = `${student.studentName || 'student'}_${selectedExamLabel}_ReportCard.pdf`.replace(/\s+/g, '_');
      const fileName = getFileNameFromDisposition(response.headers['content-disposition'], fallback);
      downloadBlobFile(response.data, fileName);
      showNotification('Report card downloaded successfully.', 'success');
    } catch (err) {
      showNotification(getApiErrorMessage(err, 'Failed to download report card.'), 'error');
    } finally {
      setDownloadingReportCard(null);
    }
  };

  const fetchStudentContacts = async (studentIds) => {
    const missingIds = studentIds.filter((studentId) => !studentContactCache[studentId]);
    if (!missingIds.length) {
      return studentContactCache;
    }

    const responses = await Promise.all(
      missingIds.map(async (studentId) => {
        try {
          const response = await axiosInstance.get(`/api/students/${studentId}`);
          return [studentId, response.data];
        } catch (err) {
          return [studentId, null];
        }
      })
    );

    const patch = Object.fromEntries(responses);
    setStudentContactCache((prev) => ({ ...prev, ...patch }));
    return { ...studentContactCache, ...patch };
  };

  const handleSendBulkSms = async () => {
    if (!selectedStudentIds.length) {
      showNotification('Select at least one student first.', 'warning');
      return;
    }
    if (!bulkSmsMessage.trim()) {
      showNotification('Message content cannot be empty.', 'warning');
      return;
    }

    try {
      setSendingBulkSms(true);
      const contacts = await fetchStudentContacts(selectedStudentIds);
      const phoneNumbers = selectedStudentIds
        .map((studentId) => contacts[studentId])
        .filter(Boolean)
        .map((student) => student.parentPhone || student.phone)
        .filter(Boolean);

      if (!phoneNumbers.length) {
        showNotification('No phone numbers found for the selected students.', 'warning');
        return;
      }

      await axiosInstance.post('/api/messaging/sms/send/bulk', {
        phoneNumbers,
        message: bulkSmsMessage.trim(),
      });

      showNotification(`SMS queued for ${phoneNumbers.length} recipients.`, 'success');
      setBulkSmsDialogOpen(false);
    } catch (err) {
      showNotification(getApiErrorMessage(err, 'Failed to send result SMS.'), 'error');
    } finally {
      setSendingBulkSms(false);
    }
  };

  const isAllVisibleSelected = activeStudentIds.length > 0 && selectedStudentIds.length === activeStudentIds.length;

  return (
    <PageContainer sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Results
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              Clean filter-first result review with instant refresh, exports, and report-card actions.
            </Typography>
          </Box>
          {routeExaminationTypeId && (
            <Button
              variant="text"
              startIcon={<WestRoundedIcon />}
              onClick={() => navigate('/academic-results')}
              sx={{ textTransform: 'none', fontWeight: 700, alignSelf: { xs: 'flex-start', lg: 'center' } }}
            >
              Back to all results
            </Button>
          )}
        </Stack>
      </Paper>

      <Snackbar open={notification.open} autoHideDuration={5000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={notification.severity} variant="filled" onClose={handleCloseNotification} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Stack spacing={1.5}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.98),
          }}
        >
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.2 }}>
            <FilterListRoundedIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Smart Filters
            </Typography>
          </Stack>

          {loadingInitialData ? (
            <Stack alignItems="center" spacing={1.2} sx={{ py: 3.5 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">Loading filters...</Typography>
            </Stack>
          ) : (
            <Grid container spacing={1.15}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="exam-results-exam-label">Exam</InputLabel>
                  <Select
                    labelId="exam-results-exam-label"
                    value={examinationTypeId}
                    label="Exam"
                    onChange={(event) => {
                      setExaminationTypeId(event.target.value);
                      setSubjectId('');
                      setSelectedStudentIds([]);
                    }}
                  >
                    <MenuItem value="">Select exam</MenuItem>
                    {examTypes.map((exam) => (
                      <MenuItem key={exam.examinationTypeId} value={String(exam.examinationTypeId)}>
                        {exam.examinationTypeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl size="small" fullWidth disabled={!examinationTypeId}>
                  <InputLabel id="exam-results-class-label">Class</InputLabel>
                  <Select
                    labelId="exam-results-class-label"
                    value={classId}
                    label="Class"
                    onChange={(event) => {
                      setClassId(event.target.value);
                      setSectionId('');
                      setSelectedStudentIds([]);
                    }}
                  >
                    {classOptions.map((item) => (
                      <MenuItem key={item.classId} value={String(item.classId)}>
                        {item.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl size="small" fullWidth disabled={!classId}>
                  <InputLabel id="exam-results-section-label">Section</InputLabel>
                  <Select
                    labelId="exam-results-section-label"
                    value={sectionId}
                    label="Section"
                    onChange={(event) => {
                      setSectionId(event.target.value);
                      setSelectedStudentIds([]);
                    }}
                  >
                    {sectionOptions.map((item) => (
                      <MenuItem key={item.sectionId} value={String(item.sectionId)}>
                        {item.sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                  <FormControl size="small" fullWidth disabled={!studentRows.length && !subjectOptions.length}>
                  <InputLabel id="exam-results-subject-label">Subject</InputLabel>
                  <Select
                    labelId="exam-results-subject-label"
                    value={subjectId}
                    label="Subject"
                    onChange={(event) => setSubjectId(event.target.value)}
                  >
                    <MenuItem value="">All subjects</MenuItem>
                    {subjectOptions.map((item) => (
                      <MenuItem key={item.subjectId} value={String(item.subjectId)}>
                        {item.subjectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={8} lg={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Student name"
                  placeholder="Search by name, roll no, subject"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Roll number"
                  placeholder="Filter by roll no"
                  value={rollNumberFilter}
                  onChange={(event) => setRollNumberFilter(event.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="exam-results-rank-label">Rank view</InputLabel>
                  <Select
                    labelId="exam-results-rank-label"
                    value={rankBucket}
                    label="Rank view"
                    onChange={(event) => setRankBucket(event.target.value)}
                  >
                    <MenuItem value="ALL">All ranks</MenuItem>
                    <MenuItem value="TOP_3">Top 3</MenuItem>
                    <MenuItem value="TOP_10">Top 10</MenuItem>
                    <MenuItem value="TOP_20">Top 20</MenuItem>
                    <MenuItem value="BOTTOM_10">Bottom 10</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="exam-results-sort-label">Order by</InputLabel>
                  <Select
                    labelId="exam-results-sort-label"
                    value={sortBy}
                    label="Order by"
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <MenuItem value="RANK">Rank</MenuItem>
                    <MenuItem value="ROLL">Roll Number</MenuItem>
                    <MenuItem value="NAME">Student Name</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Paper>

        <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 0.8, borderRadius: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, nextValue) => setActiveTab(nextValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 0,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 700,
                  },
                }}
              >
                <Tab value="results" label="Results Table" />
                <Tab value="analytics" label="Class Analytics" />
              </Tabs>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', xl: 'row' }} justifyContent="space-between" spacing={1.25}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedExamDetails
                      ? `${selectedExamLabel} • ${formatDateRange(selectedExamDetails.startDate, selectedExamDetails.endDate)}`
                      : 'Select an exam, class, and section to load results.'}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={downloadingMarksheetPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfRoundedIcon />}
                    onClick={handleDownloadMarksheetPdf}
                    disabled={!allResults.length || downloadingMarksheetPdf}
                    sx={{ textTransform: 'none' }}
                  >
                    PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={downloadingMarksheetExcel ? <CircularProgress size={16} color="inherit" /> : <TableChartRoundedIcon />}
                    onClick={handleDownloadMarksheetExcel}
                    disabled={!allResults.length || downloadingMarksheetExcel}
                    sx={{ textTransform: 'none' }}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartRoundedIcon />}
                    onClick={handleExportVisibleRows}
                    disabled={!studentRows.length}
                    sx={{ textTransform: 'none' }}
                  >
                    Export View
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                <Button
                    size="small"
                    variant={isAllVisibleSelected ? 'contained' : 'outlined'}
                    onClick={toggleSelectAllVisible}
                    disabled={!activeStudentIds.length}
                    sx={{ textTransform: 'none' }}
                  >
                    {isAllVisibleSelected ? 'Clear selection' : 'Select All'}
                  </Button>
                  {activeTab === 'results' ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SmsRoundedIcon />}
                        onClick={() => setBulkSmsDialogOpen(true)}
                        disabled={!selectedCount}
                        sx={{ textTransform: 'none' }}
                      >
                        Send result via SMS
                      </Button>
                      <Tooltip title="Email report card workflow is not configured in the backend yet.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EmailRoundedIcon />}
                            disabled
                            sx={{ textTransform: 'none' }}
                          >
                            Email report card
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>

            {activeTab === 'results' ? (
              <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                <StudentResultsSimpleTable
                  loading={loadingResults}
                  rows={studentRows}
                  selectedStudentIds={selectedStudentIds}
                  onToggleStudent={toggleStudentSelection}
                  onToggleAll={toggleSelectAllVisible}
                  allVisibleSelected={isAllVisibleSelected}
                  searchTerm={deferredSearchTerm}
                  downloadingReportCard={downloadingReportCard}
                  onPrintReportCard={handleDownloadReportCard}
                  onViewStudent={(row) => setDetailStudent(row.studentResult)}
                />
              </Paper>
            ) : null}

            {activeTab === 'analytics' && !!allResults.length ? (
              <ClassAnalyticsPanel analytics={analytics} students={studentRows} />
            ) : null}
        </Stack>
      </Stack>

      <Dialog open={bulkSmsDialogOpen} onClose={() => !sendingBulkSms && setBulkSmsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Result via SMS</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Alert severity="info">
              This sends one common result notification message to the selected students&apos; parent/mobile numbers.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Selected students: {selectedCount}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="SMS message"
              value={bulkSmsMessage}
              onChange={(event) => setBulkSmsMessage(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSmsDialogOpen(false)} disabled={sendingBulkSms}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sendingBulkSms ? <CircularProgress size={16} color="inherit" /> : <SmsRoundedIcon />}
            onClick={handleSendBulkSms}
            disabled={sendingBulkSms || !selectedCount}
          >
            {sendingBulkSms ? 'Sending...' : 'Send SMS'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailStudent)} onClose={() => setDetailStudent(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detailStudent?.studentName || 'Student'} Result Details</DialogTitle>
        <DialogContent dividers>
          {detailStudent && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Admission No: {getSafe(detailStudent.admissionNumber)} • Overall: {typeof detailStudent.percentage === 'number' ? `${detailStudent.percentage.toFixed(1)}%` : '-'} • Grade: {getSafe(detailStudent.overallGrade)}
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <StudentResultsSimpleTable
                  loading={false}
                  rows={(detailStudent.subjectResults || []).map((subject) => ({
                    rowId: `${detailStudent.studentId}-${subject.subjectId || subject.subjectName}`,
                    studentId: detailStudent.studentId,
                    studentName: detailStudent.studentName,
                    profileImageUrl: detailStudent.profileImageUrl,
                    admissionNumber: detailStudent.admissionNumber || '-',
                    rollNumber: detailStudent.rollNumber || detailStudent.admissionNumber || '-',
                    classSection: `${selectedClassName} • ${selectedSectionName}`,
                    subjectId: String(subject.subjectId || subject.subjectName),
                    subjectName: subject.subjectName || 'Subject',
                    marksObtained: subject.marksObtained,
                    totalMarks: subject.maxMarks,
                    percentage: typeof subject.percentage === 'number'
                      ? subject.percentage
                      : (typeof subject.marksObtained === 'number' && typeof subject.maxMarks === 'number' && subject.maxMarks > 0
                        ? (subject.marksObtained / subject.maxMarks) * 100
                        : null),
                    grade: subject.grade || detailStudent.overallGrade || '-',
                    status: deriveSubjectStatus(subject, detailStudent),
                    studentResult: detailStudent,
                    subjectResult: subject,
                  }))}
                  compact
                  selectedStudentIds={[]}
                  onToggleStudent={() => {}}
                  onToggleAll={() => {}}
                  allVisibleSelected={false}
                  searchTerm=""
                  downloadingReportCard={downloadingReportCard}
                  onPrintReportCard={handleDownloadReportCard}
                  onViewStudent={() => {}}
                />
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailStudent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}

export default ExamResults;
